import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import LegacySelect from 'react-select-legacy';
import { useTranslation } from 'react-i18next';
import FilterCheckbox from './FilterCheckbox';
import ASQ_SE2Logo from '/assets/images/asq_se2-logo.svg';
import ASQ_3Logo from '/assets/images/asq_3-logo.svg';
import Search from './Search';
import ReportDownload from '../helpers/ReportDownload';
import { debounce } from 'lodash';
import { groupByDisabled } from 'utils/select';
import DateRange from '../../components/filters/DateRange';

const ASQFilter = ({
  meta,
  onAsqTypeChange,
  onRecentChange,
  onWithConcernOnlyChange,
  onWithMonitorOrReferChange,
  onCSVEnqueue,
  onFollowUpCSVEnqueue,
  onReportDetailsShow,
  csvReport,
  followUpCsvReport,
  cohorts,
  onCohortChange,
  groups,
  onGroupChange,
  subgroups,
  onSubgroupChange,
  players,
  onPlayerChange,
  onPlayerFilter,
  onPlayerFilterById,
  onFollowUpSelectChange,
  filterOptions,
  isLoading,
  onFinishedAtChange,
}) => {
  const { t } = useTranslation();
  const ASQ_TYPES = [
    { value: 'asq_3', label: 'ASQ®-3' },
    { value: 'asq_se2', label: 'ASQ®:SE-2' },
  ];
  const FOLLOW_UP_OPTIONS = [
    { value: 'completed', label: t('filters.completedFollowUp') },
    { value: 'incomplete', label: t('filters.incompleteFollowUp') },
  ];
  useEffect(() => {
    onPlayerFilterById([meta.player])
  }, [onPlayerFilterById, meta.player])

  const onRecentChangeDebounced = debounce(onRecentChange, 500);
  const onWithConcernOnlyChangeDebounced = debounce(onWithConcernOnlyChange, 500);
  const onWithMonitorOrReferChangeDebounced = debounce(onWithMonitorOrReferChange, 500);

  return (
    <div>
      <div className='row u-margin-top--base'>
        <div className='column small-8'>
          <Search
            value={meta.player}
            options={players}
            onChange={onPlayerChange}
            onFilter={onPlayerFilter}
            filterOptions={filterOptions}
            disabled={isLoading}
          />
        </div>
        <div className='column small-4'>
          <DateRange
            startDate={meta.finishedAtStart}
            endDate={meta.finishedAtEnd}
            onDatesChange={onFinishedAtChange}
            periods={[]}
            disabled={isLoading}
            startDatePlaceholderText={t('filters.finishedAtStart')}
            endDatePlaceholderText={t('filters.finishedAtEnd')}
          />
        </div>
      </div>
      <div className='row u-margin-bottom--base'>
        {groups.length > 0 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-group-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={groups.filter(v => (meta.groups || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onGroupChange(v.map(i => i.value).join(','))}
              options={groups}
              placeholder={t('filters.allGroups')}
              isDisabled={isLoading}
            />
          </div>
        }
        {subgroups.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={subgroups.filter(v => (meta.subgroups || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onSubgroupChange(v.map(i => i.value).join(','))}
              options={groupByDisabled(subgroups)}
              placeholder={t('filters.allSubgroups')}
              isDisabled={isLoading}
            />
          </div>
        }
        {cohorts.length > 0 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={cohorts.filter(v => (meta.cohorts || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onCohortChange(v.map(i => i.value).join(','))}
              options={groupByDisabled(cohorts)}
              placeholder={t('filters.allCohorts')}
              isDisabled={isLoading}
            />
          </div>
        }
        <div className='column small-12 medium-4 large-3 u-margin-top--base'>
          <LegacySelect
            name='sp-asq-select'
            className={'o-select'}
            clearable={true}
            multi={false}
            value={meta.asq || ''}
            onChange={onAsqTypeChange}
            placeholder={t('filters.allAsqTypes')}
            options={ASQ_TYPES}
            simpleValue={true}
            disabled={isLoading}
          />
        </div>
        {meta.followUpEnabled ? (
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <LegacySelect
              name='sp-follow-up-select'
              className={'o-select o-select--optgroup'}
              multi={false}
              value={meta.followUpSelect || ''}
              onChange={onFollowUpSelectChange}
              options={FOLLOW_UP_OPTIONS}
              placeholder={t('filters.allAsqFollowUp')}
              simpleValue={true}
              clearable={true}
              disabled={isLoading}
            />
          </div>
        ) : <div className='column small-12 medium-4 large-3 u-margin-top--base' />}
        <div className='column shrink align-self-middle u-margin-top--base'>
          <FilterCheckbox
            label={t('filters.onlyShowMostRecent')}
            value={meta.recent || false}
            onChange={onRecentChangeDebounced}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink align-self-middle u-margin-top--base'>
          <FilterCheckbox
            label={t('filters.onlyShowWithConcern')}
            value={meta.concern || false}
            onChange={onWithConcernOnlyChangeDebounced}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink align-self-middle u-margin-top--base'>
          <FilterCheckbox
            label={t('filters.onlyShowMonitorOrRefer')}
            value={meta.monitorOrRefer || false}
            onChange={onWithMonitorOrReferChangeDebounced}
            disabled={isLoading}
          />
        </div>
      </div>
      <div className='row u-margin-bottom--small'>
        <div className='column shrink'>
          <div>
            {t('filters.generateAsqResultsCsv')}
            <a onClick={() => { onCSVEnqueue('asq_se2') }}>
              <img style={{ height: '16px', marginLeft: '0.4rem' }} src={ASQ_SE2Logo} alt="asq2 report" />
            </a>

            <a onClick={() => { onCSVEnqueue('asq_3') }}>
              <img style={{ height: '16px', marginTop: '0.09rem', marginLeft: '0.4rem' }} src={ASQ_3Logo} alt="asq3 report" />
            </a>
          </div>
          {meta.followUpEnabled && (
            <div>
              {t('filters.generateFollowUpCsv')}
              <a onClick={() => { onFollowUpCSVEnqueue('asq_se2') }}>
                <img style={{ height: '16px', marginLeft: '0.4rem' }} src={ASQ_SE2Logo} alt="asq2 report" />
              </a>

              <a onClick={() => { onFollowUpCSVEnqueue('asq_3') }}>
                <img style={{ height: '16px', marginTop: '0.09rem', marginLeft: '0.4rem' }} src={ASQ_3Logo} alt="asq3 report" />
              </a>
            </div>
          )}
        </div>
        <div className='column grow'>
          <ReportDownload report={csvReport} onReportDetailsShow={onReportDetailsShow('ASQ_SURVEYS_REPORT_DETAILS')} />
          {meta.followUpEnabled && (
            <ReportDownload report={followUpCsvReport} onReportDetailsShow={onReportDetailsShow('FOLLOW_UP_SURVEYS_REPORT_DETAILS')} />
          )}
        </div>
      </div>
    </div>
  );
}

ASQFilter.propTypes = {
  auth: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  cohorts: PropTypes.arrayOf(PropTypes.object).isRequired,
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  subgroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  players: PropTypes.arrayOf(PropTypes.object),
  teachers: PropTypes.arrayOf(PropTypes.object).isRequired,
  csvReport: PropTypes.object,
  followUpCsvReport: PropTypes.object,
  withScope: PropTypes.object.isRequired,
  filterOptions: PropTypes.func.isRequired,
  onAsqTypeChange: PropTypes.func.isRequired,
  onCohortChange: PropTypes.func.isRequired,
  onSubgroupChange: PropTypes.func.isRequired,
  onGroupChange: PropTypes.func.isRequired,
  onCSVEnqueue: PropTypes.func.isRequired,
  onFollowUpCSVEnqueue: PropTypes.func.isRequired,
  onReportDetailsShow: PropTypes.func.isRequired,
  onPlayerChange: PropTypes.func.isRequired,
  onPlayerFilter: PropTypes.func.isRequired,
  onPlayerFilterById: PropTypes.func.isRequired,
  onRecentChange: PropTypes.func.isRequired,
  onWithConcernOnlyChange: PropTypes.func.isRequired,
  onWithMonitorOrReferChange: PropTypes.func.isRequired,
  onFollowUpSelectChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onFinishedAtChange: PropTypes.func.isRequired,
}

export default ASQFilter;
